import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/ui/Header';
import StatsCard from './components/StatsCard';
import OrdersChart from './components/OrdersChart';
import TopProductsTable from './components/TopProductsTable';
import UserActivityChart from './components/UserActivityChart';
import LoadingSpinner from '../login-authentication/components/LoadingSpinner';
import statisticsService from '../../services/statisticsService';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Package, 
  Users, 
  Calendar,
  Download,
  RefreshCw,
  Trash2,
  RotateCcw
} from 'lucide-react';

const StatisticsDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0], // 30 days ago
    endDate: new Date()?.toISOString()?.split('T')?.[0] // today
  });
  const [refreshing, setRefreshing] = useState(false);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/product-order-management');
      return;
    }
  }, [userProfile, navigate]);

  // Load statistics
  useEffect(() => {
    loadStatistics();
  }, [dateRange, selectedUserId]);

  useEffect(() => {
    const loadAvailableUsers = async () => {
      const result = await statisticsService.getAvailableUsers();
      if (result?.success) {
        setAvailableUsers(result.users || []);
      }
    };

    loadAvailableUsers();
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    setError('');
    const userFilter = selectedUserId === 'all' ? null : selectedUserId;
    
    try {
      // Load statistics
      const statsResult = await statisticsService?.getOrdersStatistics(
        dateRange?.startDate,
        dateRange?.endDate,
        userFilter
      );
      
      if (statsResult?.success) {
        setStatistics(statsResult?.statistics);
      } else {
        setError(statsResult?.error || 'Errore nel caricare le statistiche');
      }

      // 🚨 ENHANCED: Always load diagnostic info to show status breakdown
      const diagnosticResult = await statisticsService?.diagnoseOrderVisibility(userFilter);
      if (diagnosticResult?.success) {
        setStatusBreakdown(diagnosticResult?.statusBreakdown);
      }

      // Load recent orders (WhatsApp orders only)
      const ordersResult = await statisticsService?.getRecentOrders(10, userFilter);
      if (ordersResult?.success) {
        setRecentOrders(ordersResult?.orders || []);
      } else {
        console.log('Recent orders error:', ordersResult?.error);
      }

      // Load top products using enhanced function
      const topProductsResult = await statisticsService?.getTopSellingProducts(
        dateRange?.startDate,
        dateRange?.endDate,
        10,
        userFilter
      );
      if (topProductsResult?.success) {
        setTopProducts(topProductsResult?.products || []);
      }

    } catch (error) {
      setError('Errore di connessione nel caricare i dati');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadStatistics();
    setRefreshing(false);
  };

  // Reset all statistics
  const handleResetStatistics = async () => {
    if (!confirm('Sei sicuro di voler azzerare tutte le statistiche? Questa azione eliminerà tutti gli ordini e non può essere annullata.')) {
      return;
    }

    setResetLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await statisticsService?.resetAllStatistics();
      
      if (result?.success) {
        setSuccessMessage(result?.message || 'Statistiche azzerate con successo');
        await loadStatistics(); // Reload data
      } else {
        setError(result?.error || 'Errore nell\'azzerare le statistiche');
      }
    } catch (error) {
      setError('Errore di connessione nell\'azzerare le statistiche');
    } finally {
      setResetLoading(false);
    }
  };

  // Delete individual order
  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Sei sicuro di voler eliminare questo ordine? Le statistiche verranno aggiornate automaticamente.')) {
      return;
    }

    try {
      const result = await statisticsService?.deleteOrder(orderId);
      
      if (result?.success) {
        setSuccessMessage(result?.message || 'Ordine eliminato con successo');
        await loadStatistics(); // Reload data
      } else {
        setError(result?.error || 'Errore nell\'eliminare l\'ordine');
      }
    } catch (error) {
      setError('Errore di connessione nell\'eliminare l\'ordine');
    }
  };

  const handleExportData = () => {
    const selectedUser = selectedUserId === 'all'
      ? null
      : availableUsers.find((user) => user.id === selectedUserId);

    const rows = [
      ['Filtro utente', selectedUser ? selectedUser.full_name || selectedUser.username : 'Tutti'],
      ['Data inizio', dateRange.startDate],
      ['Data fine', dateRange.endDate],
      ['Ordini totali', statistics?.totalOrders || 0],
      ['Fatturato', statistics?.totalRevenue || 0],
      ['Prodotti venduti', statistics?.totalProducts || 0],
      ['Trend mensile %', statistics?.monthlyTrendPercentage || 0],
      [],
      ['Top prodotti'],
      ['Prodotto', 'Quantita', 'Fatturato']
    ];

    topProducts.forEach((product) => {
      rows.push([
        product?.product_name || product?.product || 'N/A',
        product?.total_quantity || product?.quantity || 0,
        product?.total_revenue || 0
      ]);
    });

    rows.push([]);
    rows.push(['Ordini recenti']);
    rows.push(['Data', 'Utente', 'Prodotti', 'Totale']);

    recentOrders.forEach((order) => {
      rows.push([
        new Date(order?.created_at)?.toLocaleDateString('it-IT'),
        order?.user_profiles?.full_name || order?.user_profiles?.username || 'N/A',
        order?.order_data?.products?.length || 0,
        order?.total_amount || 0
      ]);
    });

    const csvContent = rows
      .map((row) =>
        row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `statistiche_${selectedUser?.username || 'tutti'}_${dateRange.startDate}_${dateRange.endDate}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    setSuccessMessage('Export CSV completato con successo');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen overflow-x-clip bg-gray-50">
      {/* Header */}
      <Header 
        title="Dashboard Statistiche"
        subtitle="Analisi vendite e performance del sistema ordini"
        userProfile={userProfile}
        onLogout={handleLogout}
        actions={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <button
              onClick={handleResetStatistics}
              disabled={resetLoading}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 sm:w-auto"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${resetLoading ? 'animate-spin' : ''}`} />
              {resetLoading ? 'Azzerando...' : 'Azzera Statistiche'}
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 sm:w-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            <button
              onClick={handleExportData}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta
            </button>
            <button
              onClick={() => navigate('/user-management-dashboard')}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
            >
              <Users className="w-4 h-4 mr-2" />
              Utenti
            </button>
            <button
              onClick={() => navigate('/product-order-management')}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 sm:w-auto"
            >
              Ordini
            </button>
          </div>
        }
      />
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Range Selector */}
        <div className="mb-8 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Calendar className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Periodo di analisi:</span>
              <input
                type="date"
                value={dateRange?.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
              />
              <span className="hidden text-gray-500 sm:inline">-</span>
              <input
                type="date"
                value={dateRange?.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
              />
              <span className="text-sm font-medium text-gray-700">Venditore:</span>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:w-auto"
              >
                <option value="all">Tutti</option>
                {availableUsers
                  ?.filter((user) => user?.is_active)
                  ?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.username}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 text-green-700">
            {successMessage}
            <button 
              onClick={() => setSuccessMessage('')}
              className="ml-4 text-green-600 hover:text-green-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            {error}
            <button 
              onClick={() => setError('')}
              className="ml-4 text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {/* 🔍 ENHANCED: Always show diagnostic info to clarify order filtering */}
        {statusBreakdown && (
          <div className="mb-6 overflow-hidden p-4 bg-blue-50 border-l-4 border-blue-400">
            <h4 className="font-semibold text-blue-800 mb-2">📊 Stato Ordini nel Database</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
              {statusBreakdown?.map((status) => (
                <div key={status?.status_name} className="bg-white p-3 rounded border">
                  <div className="font-medium">
                    {status?.status_name === 'completed' ? '✅ WhatsApp (Completati)' : 
                     status?.status_name === 'printed' ? '🖨️ Stampati' : 
                     status?.status_name === 'draft' ? '📝 Bozze' : 
                     status?.status_name}
                  </div>
                  <div className="text-lg font-bold">{status?.order_count}</div>
                  {status?.latest_order && (
                    <div className="text-xs mt-1">
                      Ultimo: {new Date(status?.latest_order)?.toLocaleDateString('it-IT')}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 break-words p-2 bg-green-100 border border-green-300 rounded text-green-800 text-xs">
              <strong>✅ FILTRO ATTIVO:</strong> La sezione "Ordini Recenti" mostra solo ordini con status <strong>"completed"</strong> (inviati via WhatsApp). Gli ordini con status "printed" (stampe) non vengono inclusi.
            </div>
          </div>
        )}

        {/* ✅ Stats Cards with Real Growth Percentages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Ordini Totali"
            value={statistics?.totalOrders || 0}
            icon={<Package className="h-8 w-8" />}
            color="blue"
            change={`${statistics?.ordersGrowthPercentage >= 0 ? '+' : ''}${statistics?.ordersGrowthPercentage?.toFixed(1) || '0'}%`}
          />
          <StatsCard
            title="Fatturato"
            value={`€${statistics?.totalRevenue?.toFixed(2) || '0.00'}`}
            icon={<DollarSign className="h-8 w-8" />}
            color="green"
            change={`${statistics?.revenueGrowthPercentage >= 0 ? '+' : ''}${statistics?.revenueGrowthPercentage?.toFixed(1) || '0'}%`}
          />
          <StatsCard
            title="Prodotti Venduti"
            value={statistics?.totalProducts || 0}
            icon={<BarChart3 className="h-8 w-8" />}
            color="purple"
            change={`${statistics?.productsGrowthPercentage >= 0 ? '+' : ''}${statistics?.productsGrowthPercentage?.toFixed(1) || '0'}%`}
          />
          <StatsCard
            title="Trend Mensile"
            value={statistics?.monthlyTrendPercentage >= 0 ? '↗️ Crescita' : '↘️ Calo'}
            icon={<TrendingUp className="h-8 w-8" />}
            color="yellow"
            change={`${statistics?.monthlyTrendPercentage >= 0 ? '+' : ''}${statistics?.monthlyTrendPercentage?.toFixed(1) || '0'}%`}
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Orders Chart */}
          <OrdersChart data={statistics?.ordersByMonth || {}} />
          
          {/* User Activity Chart */}
          <UserActivityChart data={statistics?.userActivity || {}} />
        </div>

        {/* Enhanced Top Products Table */}
        <TopProductsTable products={topProducts} />

        {/* 🚨 ENHANCED: Recent Orders with clear WhatsApp filtering explanation */}
        <div className="mt-8 overflow-hidden bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 bg-green-50 px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="mr-2">📱</span>
                  Ordini Recenti WhatsApp
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  Mostra solo ordini inviati via WhatsApp (status: "completed"). Le stampe (status: "printed") sono escluse.
                </p>
              </div>
              <div className="text-sm bg-green-100 px-3 py-1 rounded-full">
                {recentOrders?.length || 0} ordini WhatsApp
              </div>
            </div>
          </div>
          <div className="space-y-3 p-4 md:hidden">
            {recentOrders?.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-gray-500">
                <span className="block text-lg">📭</span>
                <span className="mt-2 block text-sm font-medium">Nessun ordine WhatsApp trovato</span>
                <span className="mt-1 block text-xs">Solo ordini con status "completed" appaiono qui</span>
              </div>
            ) : (
              recentOrders?.map((order) => (
                <div key={order?.id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {order?.user_profiles?.full_name || 'N/A'}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {new Date(order?.created_at)?.toLocaleDateString('it-IT')}
                      </div>
                    </div>
                    <span className="inline-flex shrink-0 items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                      📱 WhatsApp
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-gray-50 p-3">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Prodotti</div>
                      <div className="mt-1 font-medium text-gray-900">{order?.order_data?.products?.length || 0}</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Totale</div>
                      <div className="mt-1 font-medium text-gray-900">€{order?.total_amount?.toFixed(2) || '0.00'}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteOrder(order?.id)}
                    className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                    title="Elimina ordine"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Elimina ordine
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prodotti
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Totale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOrders?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        <span className="block text-lg">📭</span>
                        <span className="block text-sm font-medium mt-2">Nessun ordine WhatsApp trovato</span>
                        <span className="block text-xs mt-1">Solo gli ordini con status "completed" appaiono qui</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentOrders?.map((order) => (
                    <tr key={order?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(order?.created_at)?.toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order?.user_profiles?.full_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          📱 WhatsApp
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {order?.order_data?.products?.length || 0} prodotti
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        €{order?.total_amount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDeleteOrder(order?.id)}
                          className="text-red-600 hover:text-red-900 focus:outline-none"
                          title="Elimina ordine"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDashboard;
